import Button from '@material-ui/core/Button';
import ButtonGroup from '@material-ui/core/ButtonGroup';
import Container from '@material-ui/core/Container';
import Hidden from '@material-ui/core/Hidden';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import clsx from 'clsx';
import Carousel from 'nuka-carousel';
import React from 'react';
import { useTranslation } from 'react-i18next';

import androidWrap from '../../images/white-label/brended/android.png';
import isoWrap from '../../images/white-label/brended/ios.png';
import screen1 from '../../images/white-label/brended/screen_1.png';
import screen2 from '../../images/white-label/brended/screen_2.png';
import screen3 from '../../images/white-label/brended/screen_3.png';
import screen4 from '../../images/white-label/brended/screen_4.png';
import screen5 from '../../images/white-label/brended/screen_5.png';
import screen6 from '../../images/white-label/brended/screen_6.png';
import screen7 from '../../images/white-label/brended/screen_7.png';
import screen8 from '../../images/white-label/brended/screen_8.png';
import screen9 from '../../images/white-label/brended/screen_9.png';
import screen10 from '../../images/white-label/brended/screen_10.png';
import screen11 from '../../images/white-label/brended/screen_11.png';
import screen12 from '../../images/white-label/brended/screen_12.png';
import AndroidIcon from '../icons/android';
import IOSIcon from '../icons/ios';
import SectionTitle from './section-title';

const useStyles = makeStyles(theme => ({
  root: {
    padding: theme.spacing(23, 0),
    backgroundImage:
      'radial-gradient(87.04% 212.32% at 95.46% -27.14%, #02BBD3 0%, #0030AC 100%)'
  },
  title: {
    marginBottom: theme.spacing(4),
    color: theme.palette.common.white
  },
  descr: {
    marginBottom: theme.spacing(6),
    fontSize: 16,
    color: theme.palette.common.white
  },
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'center',
    maxWidth: 240,
    marginRight: 'auto',
    marginLeft: 'auto',
    marginBottom: theme.spacing(15)
  },
  button: {
    flex: '0 0 50%',
    paddingRight: theme.spacing(3),
    paddingLeft: theme.spacing(3),
    border: `1px solid ${theme.colors.borderColor}`
  },
  primaryButton: {
    color: theme.palette.primary.main
  },
  carousel: {
    padding: theme.spacing(0, 5),
    margin: theme.spacing(6, 0),
    [theme.breakpoints.up('md')]: {
      padding: 0,
      margin: theme.spacing(25, 0)
    },
    '& .slider-control-centerleft': {
      display: 'none'
    },
    '& .slider-control-centerright': {
      display: 'none'
    },
    '& .slider-control-bottomcenter': {
      zIndex: 2,
      bottom: `${theme.spacing(-23)}px!important`,
      '& button': {
        outline: 'none'
      },
      '& svg': {
        width: 10,
        height: 10,
        verticalAlign: 'middle',
        '& circle': {
          fill: `${theme.palette.common.white}!important`,
          cx: 5,
          cy: 5,
          r: 5
        }
      }
    }
  },
  carouselWrap: {
    position: 'relative',
    maxWidth: 1366,
    margin: '0 auto'
  },
  imgWrap: {
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    width: '100%',
    height: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'block'
    }
  },
  img: {
    display: 'block',
    maxWidth: '100%',
    height: 'auto',
    borderRadius: 36,
    [theme.breakpoints.up('sm')]: {
      borderRadius: 16
    }
  },
  imgFrame: {
    position: 'absolute',
    top: '50.1%',
    left: '50%',
    width: 'auto',
    height: '101.1%',
    borderRadius: 30,
    transform: 'translate(-50%, -50%)',
    [theme.breakpoints.up('sm')]: {
      top: '50.5%',
      height: '106%'
    }
  },
  offerBlock: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center'
  },
  subTitle: {
    marginBottom: theme.spacing(6)
  }
}));

const images = [
  screen1,
  screen2,
  screen3,
  screen4,
  screen5,
  screen6,
  screen7,
  screen8,
  screen9,
  screen10,
  screen11,
  screen12
];

const BrandedAppsContent = () => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isoView, setIsoView] = React.useState(true);

  const theme = useTheme();
  const smallMatches = useMediaQuery(theme.breakpoints.up('sm'));
  const mediumMatches = useMediaQuery(theme.breakpoints.up('md'));

  const countSlides = smallMatches ? 3 : 1;

  return (
    <section className={classes.root}>
      <Container maxWidth="xl">
        <SectionTitle
          className={classes.title}
          title={t('whiteLabelPage.brandedApps.title')}
        />
        <Typography className={classes.descr} variant="body1" align="center">
          {t('whiteLabelPage.brandedApps.descr')}
        </Typography>
        <ButtonGroup
          className={classes.buttonsContainer}
          aria-label="button group"
        >
          <Button
            className={clsx(classes.button, {
              [classes.primaryButton]: isoView
            })}
            variant="contained"
            startIcon={<IOSIcon />}
            onClick={() => setIsoView(true)}
          >
            iOS
          </Button>
          <Button
            className={clsx(classes.button, {
              [classes.primaryButton]: !isoView
            })}
            variant="contained"
            startIcon={<AndroidIcon />}
            onClick={() => setIsoView(false)}
          >
            Android
          </Button>
        </ButtonGroup>
      </Container>
      <div className={classes.carouselWrap}>
        <Carousel
          className={classes.carousel}
          slidesToShow={mediumMatches ? 5 : countSlides}
          autoplay
          autoplayInterval={2000}
          wrapAround
          cellSpacing={20}
        >
          {images.map((img, indx) => {
            return (
              <div key={indx} className={classes.imgWrap}>
                <img className={classes.img} src={img} alt={`screen ${indx}`} />
                <Hidden smUp>
                  <img
                    className={classes.imgFrame}
                    src={isoView ? isoWrap : androidWrap}
                    alt=""
                  />
                </Hidden>
              </div>
            );
          })}
        </Carousel>
        <Hidden xsDown>
          <img
            className={classes.imgFrame}
            src={isoView ? isoWrap : androidWrap}
            alt=""
          />
        </Hidden>
      </div>
    </section>
  );
};

export default BrandedAppsContent;
